/**
 * Sticky Add to Cart Component
 * 
 * Shows a sticky add to cart bar when the regular add to cart button goes out of view.
 * Automatically syncs with the main product form and variant selection.
 */

// Add CSS for screen reader only class
const style = document.createElement('style');
style.textContent = `
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;
document.head.appendChild(style);

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.productFormComponent = null;
    this.intersectionObserver = null;
    this.isVisible = false;
    this.shouldBeVisible = false;
    this.addToCartButton = null;
  }

  init() {
    // Find the regular add to cart button
    this.addToCartButton = this.querySelector('button[name="add"]');
    this.shopNowButton = this.querySelector('.sticky-add-to-cart__shop-now-button');
    this.shopNowContainer = this.querySelector('.sticky-add-to-cart__content');
    if (!(this.addToCartButton instanceof HTMLButtonElement)) throw new Error('Add to cart button not found');
    this.productFormComponent = document.querySelector(`form#${this.addToCartButton.getAttribute('form')}`);
    if (!this.productFormComponent) throw new Error('Product form component not found');

    // Option selectors
    this.selectOptionSelectors = document.querySelectorAll('.variant-picker select.variant-option__select');
    this.thisOptionSelectors = this.querySelectorAll('select.variant-option__select');
    
    if (this.productFormComponent) {
      this.setupIntersectionObserver();
      this.setupEventListeners();
      this.syncVariantSelection();
      this.shopNowHandle();
      this.optionSelectorHandle();
    }
  }

  connectedCallback() {
    this.init();
  }

  setupIntersectionObserver() {
    if (!this.productFormComponent) return;
    const variantPickerForm = document.querySelector('.variant-picker__form');
    let variantPickerIntersection = false;
    let productFormIntersection = false;

    // Create intersection observer to detect when add to cart goes out of view
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.target === this.productFormComponent) {
            productFormIntersection = !entry.isIntersecting;
          }
          if (entry.target === variantPickerForm) {
            variantPickerIntersection = !entry.isIntersecting;
          }
          // Handles the edge case where there is no variant picker form
          if(!variantPickerForm) variantPickerIntersection = true;
          if (productFormIntersection && variantPickerIntersection) {
            this.shouldBeVisible = true;
          } else {
            this.shouldBeVisible = false;
          }
          this.handleVisibilityChange();
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1,0.2,0.3,0.4,0.5,0.6,0.7,0.8,0.9,1]
      }
    );

    this.intersectionObserver.observe(this.productFormComponent);

    if (variantPickerForm) {
      this.intersectionObserver.observe(variantPickerForm);
    }
  }

  setupEventListeners() {
    // Listen for variant changes in the main form - enhanced to catch more events
    const variantPickers = document.querySelectorAll('input[name="id"], select[name="id"], select[name^="options["], input[type="radio"][name^="options["]');
    
    variantPickers.forEach((picker) => {
      picker.addEventListener('change', (e) => {
        setTimeout(() => this.syncVariantSelection(), 50); // Increased delay to ensure DOM updates
      });
    });

    // Also listen for theme-specific radio buttons (flavour-blockId-productId pattern)
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      const blockId = variantPicker.getAttribute('data-block-id');
      const productId = variantPicker.getAttribute('data-product-id');
      
      if (blockId && productId) {
        // Listen for any radio button that matches the theme naming pattern
        const themeRadioButtons = document.querySelectorAll(`input[type="radio"][name*="-${blockId}-${productId}"]`);
        
        themeRadioButtons.forEach((radio) => {
          radio.addEventListener('change', () => {
            setTimeout(() => this.syncVariantSelection(), 50);
          });
        });
      }
    }

    // Listen for variant-picker specific events (most reliable)
    if (variantPicker) {
      // Listen for the VariantSelectedEvent and VariantUpdateEvent from variant-picker
      variantPicker.addEventListener('variant-selected', () => {
        setTimeout(() => this.syncVariantSelection(), 50);
      });
      
      variantPicker.addEventListener('variant-update', () => {
        setTimeout(() => this.syncVariantSelection(), 50);
      });
      
      // Also listen for direct change events on the variant-picker
      variantPicker.addEventListener('change', () => {
        setTimeout(() => this.syncVariantSelection(), 50);
      });
    }
    
    // Also listen for custom variant events (fallback)
    document.addEventListener('variant:change', () => {
      setTimeout(() => this.syncVariantSelection(), 50);
    });

    // Listen for product form variant updates (gets actual selected variant with correct image)
    document.addEventListener('product-form:variant:update', (event) => {
      // @ts-ignore - Custom event with detail property
      if (event.detail && event.detail.resource) {
        // @ts-ignore - Custom event with detail property
        this.updateImagesFromVariant(event.detail.resource);
        // @ts-ignore - Custom event with detail property
        this.updateFlavourTextFromVariant(event.detail.resource);
        // @ts-ignore - Custom event with detail property
        this.updatePricingFromVariant(event.detail.resource);
        this.updateButtonState();
      }
    });

    // Also listen for the main variant update event
    document.addEventListener('variant:update', (event) => {
      // @ts-ignore - Custom event with detail property
      if (event.detail && event.detail.resource) {
        // @ts-ignore - Custom event with detail property
        this.updateImagesFromVariant(event.detail.resource);
        // @ts-ignore - Custom event with detail property
        this.updateFlavourTextFromVariant(event.detail.resource);
        // @ts-ignore - Custom event with detail property
        this.updatePricingFromVariant(event.detail.resource);
        this.updateButtonState();
      }
    });

    // Listen for subscription price updates from the main section
    document.addEventListener('subscription-price-updated', () => {
      console.log('📣 [Sticky Cart] Heard subscription-price-updated event, will update prices in 50ms');
      // Wait a bit for DOM to update, then refresh sticky cart prices
      setTimeout(() => {
        this.updateSubscriptionPricing();
      }, 50);
    });

    // Listen for variant changes in the sticky bar
    const stickyVariantPickers = this.querySelectorAll('select[data-option-name]');
    
    stickyVariantPickers.forEach((picker) => {
      picker.addEventListener('change', (event) => {
        const target = event.target;
        if(target instanceof HTMLSelectElement && target.value) {
          // Don't call updateStickyVariantImage here - let events handle it to avoid flash
          this.syncMainVariantSelection(target.dataset.optionName ?? '', target.value);
          this.updateSubscriptionPricing();
          this.disableAtcButton();
          
          // Update flavour name when sticky option changes
          this.updateSelectedFlavourFromSticky(target);
        }
      });
    });

    // Listen for subscription changes in the sticky bar
    this.setupSubscriptionListeners();
    
    // Listen for subscription changes in the MAIN form
    this.setupMainSubscriptionListeners();

    // Listen for form submission to hide sticky bar temporarily
    const productForm = document.querySelector('form[action*="/cart/add"]');
    
    if (productForm) {
      productForm.addEventListener('submit', () => {
        this.hideTemporarily();
      });
    }

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', () => {
        if (!this.addToCartButton) return;
        this.addToCartButton.classList.add('atc-added');
        setTimeout(() => {
          if (!this.addToCartButton) return;
          this.addToCartButton.classList.remove('atc-added');
        }, 2000);
      });
    }
  }

  enableAtcButton() {
    if (!(this.addToCartButton instanceof HTMLButtonElement)) return;
    this.addToCartButton.disabled = false;
  }
  disableAtcButton() {
    if (!(this.addToCartButton instanceof HTMLButtonElement)) return;
    this.addToCartButton.disabled = true;
  }

  handleVisibilityChange() {
    if (this.shouldBeVisible) {
      this.show();
    } else {
      this.hide();
    }
  }

  show() {
    this.hidden = false;
    this.setAttribute('data-visible', 'true');
    
    // Add body padding to prevent content from being hidden behind sticky bar
    const stickyHeight = this.offsetHeight;
    document.body.style.paddingBottom = `${stickyHeight}px`;
    
    // Sync variants when showing
    this.syncVariantSelection();
    
    // Announce to screen readers
    this.announceToScreenReader('Sticky add to cart bar is now visible');
  }

  hide() {
    this.setAttribute('data-visible', 'false');
    
    // Remove body padding
    document.body.style.paddingBottom = '';
    
    // Hide after animation completes
    setTimeout(() => {
      if (!this.shouldBeVisible) {
        this.hidden = true;
      }
    }, 300);
    
    // Announce to screen readers
    this.announceToScreenReader('Sticky add to cart bar is now hidden');
  }

  hideTemporarily() {
    // Hide sticky bar temporarily during form submission
    this.hide();
    
    // Show again after a delay
    setTimeout(() => {
      this.updateVisibility();
    }, 1000);
  }

  updateVisibility() {
    if(this.shouldBeVisible) {
      this.show();
    } else {
      this.hide();
    }
  }

  syncVariantSelection() {
    
    // Get all sticky dropdowns
    const stickySelectors = this.querySelectorAll('select[data-option-name]');
    
    stickySelectors.forEach((stickySelector) => {
      if(!(stickySelector instanceof HTMLSelectElement)) return;
      const optionName = stickySelector.getAttribute('data-option-name');
      
      // Find any checked radio button that matches this option name
      const checkedRadio = document.querySelector(`input[type="radio"]:checked[name*="${optionName}"]`);

      if (checkedRadio instanceof HTMLInputElement && checkedRadio.value !== stickySelector.value) {
        const oldValue = stickySelector.value;
        stickySelector.value = checkedRadio.value;
      }
    });
    
    // Update button state, pricing, and flavour display
    this.updateButtonState();
    this.updateSubscriptionPricing();

    // Update flavour display when main section changes
    this.updateFlavourFromMainSection();
    
    // Update images from main gallery when syncing
    this.syncImagesFromMainForm();
  }

  /**
   * Updates images from a variant resource (from variant update events)
   * This is the most reliable way to get the correct image for products with multiple options
   * @param {any} variant - The variant resource from the event
   */
  updateImagesFromVariant(variant) {
    // @ts-ignore - Variant object from Shopify API
    if (!variant || !variant.featured_media) return;
    
    // @ts-ignore - Variant object from Shopify API
    const featuredMedia = variant.featured_media;
    const imageUrl = featuredMedia.preview_image?.src;
    
    if (!imageUrl) return;
    
    // Generate different sizes for different contexts
    const dropdownImageUrl = imageUrl + '&width=100';
    const productImageUrl = imageUrl + '&width=60';
    // @ts-ignore - Variant object from Shopify API
    const imageAlt = variant.title || variant.name || 'Product image';
    
    // Update ALL dropdown swatch images
    const imageContainers = this.querySelectorAll('.variant-option__selected-image');
    imageContainers.forEach((imageContainer) => {
      imageContainer.innerHTML = `<img src="${dropdownImageUrl}" alt="${imageAlt}" class="sticky-variant-image" />`;
    });
    
    // Update ALL product image containers (both mobile-only and regular)
    const productImageContainers = this.querySelectorAll('.sticky-add-to-cart__product-image');
    productImageContainers.forEach((productImageContainer) => {
      const productImage = productImageContainer.querySelector('.sticky-product-image');
      if (productImage instanceof HTMLImageElement) {
        productImage.src = productImageUrl;
        productImage.srcset = productImageUrl;
        productImage.alt = imageAlt;
      }
    });
  }

  /**
   * Syncs images from the main product form/gallery
   * Used when sticky bar first shows or when syncing from main form
   */
  syncImagesFromMainForm() {
    // Try to get the currently displayed image from the main product gallery
    const mainGalleryImage = document.querySelector('media-gallery img.product__media-item, .product__media img, [data-product-media-viewer] img');
    
    if (mainGalleryImage instanceof HTMLImageElement && mainGalleryImage.src) {
      const imageUrl = mainGalleryImage.src.split('?')[0]; // Remove query params
      const imageAlt = mainGalleryImage.alt || 'Product image';
      
      // Generate different sizes for different contexts
      const dropdownImageUrl = imageUrl + '?width=100';
      const productImageUrl = imageUrl + '?width=60';
      
      // Update ALL dropdown swatch images
      const imageContainers = this.querySelectorAll('.variant-option__selected-image');
      imageContainers.forEach((imageContainer) => {
        imageContainer.innerHTML = `<img src="${dropdownImageUrl}" alt="${imageAlt}" class="sticky-variant-image" />`;
      });
      
      // Update ALL product image containers (both mobile-only and regular)
      const productImageContainers = this.querySelectorAll('.sticky-add-to-cart__product-image');
      productImageContainers.forEach((productImageContainer) => {
        const productImage = productImageContainer.querySelector('.sticky-product-image');
        if (productImage instanceof HTMLImageElement) {
          productImage.src = productImageUrl;
          productImage.srcset = productImageUrl;
          productImage.alt = imageAlt;
        }
      });
    }
  }

  /**
   * Updates flavor text from a variant resource (from variant update events)
   * @param {any} variant - The variant resource from the event
   */
  updateFlavourTextFromVariant(variant) {
    // @ts-ignore - Variant object from Shopify API
    if (!variant || !variant.title) return;
    
    // Skip if it's the default variant
    // @ts-ignore - Variant object from Shopify API
    if (variant.title === 'Default Title') return;
    
    // Update ALL flavour labels in the sticky bar (both mobile and desktop)
    const flavourLabels = this.querySelectorAll('.flavour-label');
    
    // @ts-ignore - Variant object from Shopify API
    const variantTitle = variant.title;
    
    flavourLabels.forEach((flavourLabel) => {
      if (flavourLabel && variantTitle) {
        flavourLabel.textContent = variantTitle;
      }
    });
  }

  /**
   * Updates pricing from a variant resource (from variant update events)
   * This updates both one-time and subscription prices in the sticky bar
   * @param {any} variant - The variant resource from the event
   */
  updatePricingFromVariant(variant) {
    // @ts-ignore - Variant object from Shopify API
    if (!variant || !variant.price) return;
    
    // // Get variant price in cents
    // // @ts-ignore - Variant object from Shopify API
    // const currentVariantPrice = variant.price;
    
    // // Get subscription discount from the sticky container data attribute
    // const subscriptionDiscount = parseInt(this.getAttribute('data-subscription-discount') || '20');
    // const discountMultiplier = (100 - subscriptionDiscount) / 100;
    // const subscriptionPrice = Math.round(currentVariantPrice * discountMultiplier);
    
    // // Update ALL one-time price elements (in case there are multiple)
    // Update ALL one-time price elements immediately
    const allOneTimePrices = this.querySelectorAll('.subscription-price.one-time-price');
    const newOnetimePrice = this.formatMoney(variant.price);
    
    allOneTimePrices.forEach((el) => {
      el.textContent = newOnetimePrice;
    });

    // Wait a bit for the main section to update, then read subscription price from it
    setTimeout(() => {
      this.updateSubscriptionPricing();
    }, 100);
    //Wrong price showing for sticky add to cart 
    // // Update subscription price (inside label for sticky-subscription)
    // const subscriptionPriceElement = this.querySelector('label[for="sticky-subscription"] .subscription-price');
    // if (subscriptionPriceElement) {
    //   subscriptionPriceElement.textContent = this.formatMoney(subscriptionPrice);
    // }

    // // Update subscription original (slashed) price (inside label for sticky-subscription)
    // const subscriptionOriginalPriceElement = this.querySelector('label[for="sticky-subscription"] .subscription-original-price');
    // if (subscriptionOriginalPriceElement) {
    //   subscriptionOriginalPriceElement.textContent = this.formatMoney(currentVariantPrice);
    // }
  }

  updateAllVariantImages() {
    // This method is now replaced by syncImagesFromMainForm
    // Keeping for backward compatibility
    this.syncImagesFromMainForm();
  }

  /**
   * 
   * @param {HTMLSelectElement} selectElement 
   */
  updateAllImageContainers(selectElement) {
    // Only update image for flavour option
    const optionName = selectElement.getAttribute('data-option-name');
    
    if (optionName && optionName.toLowerCase() === 'flavour') {
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      if (selectedOption) {
        const optionValue = selectedOption.value;
        const imageUrl = selectedOption.getAttribute('data-variant-image-url');
        const imageAlt = selectedOption.getAttribute('data-variant-image-alt') || optionValue;
        const variantTitle = selectedOption.getAttribute('data-variant-title') || optionValue;
        
        if (imageUrl) {
          // Update ALL image containers in the sticky bar (just like flavour labels)
          const imageContainers = this.querySelectorAll('.variant-option__selected-image');
          
          imageContainers.forEach((imageContainer) => {
            imageContainer.innerHTML = `<img src="${imageUrl}" alt="${imageAlt}" class="sticky-variant-image" />`;
          });

          // Update ALL product image containers (both mobile-only and regular)
          const productImageContainers = this.querySelectorAll('.sticky-add-to-cart__product-image');
          
          productImageContainers.forEach((productImageContainer) => {
            const productImage = productImageContainer.querySelector('.sticky-product-image');
            if (productImage instanceof HTMLImageElement) {
              productImage.src = imageUrl;
              productImage.srcset = imageUrl;
              productImage.alt = variantTitle;
            }
          });
        } else {
          console.warn('❌ StickyAddToCart: No image URL found for variant:', optionValue);
          
          // Update ALL image containers with placeholder
          const imageContainers = this.querySelectorAll('.variant-option__selected-image');
          imageContainers.forEach((imageContainer) => {
            imageContainer.innerHTML = '<div class="sticky-variant-placeholder"></div>';
          });
        }
      }
    }
  }

  // Keep the old method for backward compatibility but rename it
  /**
   * 
   * @param {HTMLSelectElement} selectElement 
   */
  updateStickyVariantImage(selectElement) {
    // This method now just calls the new method
    this.updateAllImageContainers(selectElement);
  }

  setupSubscriptionListeners() {
    // Listen for subscription radio button changes
    const subscriptionRadios = this.querySelectorAll('input[name="sticky_purchase_type"]');
    subscriptionRadios.forEach((radio) => {
      radio.addEventListener('change', (event) => {
        if(!(event.target instanceof HTMLInputElement)) return;
        this.handleSubscriptionChange(event.target);
      });
    });

    // Simple: Don't sync from main on load, let Liquid default handle it
  }

  setupMainSubscriptionListeners() {
    // Listen for subscription radio button changes in the MAIN form
    const mainSubscriptionRadios = document.querySelectorAll('input[name="purchase_type"]');
    mainSubscriptionRadios.forEach((radio) => {
      radio.addEventListener('change', (event) => {
        if(!(event.target instanceof HTMLInputElement)) return;
        this.syncSubscriptionFromMain(event.target);
      });
    });
  }

  syncSubscriptionFromMain(mainRadio) {
    const isSubscription = mainRadio.value === 'autodeliver';
    // Update sticky radio buttons
    const stickyOneTimeRadio = this.querySelector('input[name="sticky_purchase_type"][value="onetime"]');
    const stickySubscriptionRadio = this.querySelector('input[name="sticky_purchase_type"][value="subscription"]');
    if (isSubscription && stickySubscriptionRadio) {
      stickySubscriptionRadio.checked = true;
    } else if (!isSubscription && stickyOneTimeRadio) {
      stickyOneTimeRadio.checked = true;
    }
    // Update pricing display
    this.updateSubscriptionPricing();
  }

  /**
   * 
   * @param {HTMLInputElement} radio 
   */
  handleSubscriptionChange(radio) {
    if(!(radio instanceof HTMLInputElement)) return;
    let isSubscription = radio.value === 'subscription';
    
    const mainPurchaseTypeRadio = document.querySelector('input#rc_purchase_type_onetime');

    if (!mainPurchaseTypeRadio) {
      isSubscription = false;
    } 
    // Sync with main form
    this.syncSubscriptionToMain(isSubscription);
    
    // Update pricing display
    this.updateSubscriptionPricing();
  }

  /**
   * 
   * @param {boolean} isSubscription 
   */
  syncSubscriptionToMain(isSubscription) {
    // Find main subscription radio buttons
    const mainOneTimeRadio = document.querySelector('input[name="purchase_type"][value=""]');
    const mainSubscriptionRadio = document.querySelector('input[name="purchase_type"][value="autodeliver"]');
    
    if (isSubscription && mainSubscriptionRadio instanceof HTMLInputElement) {
      mainSubscriptionRadio.checked = true;
      mainSubscriptionRadio.click();
      mainSubscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (!isSubscription && mainOneTimeRadio instanceof HTMLInputElement) {
      mainOneTimeRadio.checked = true;
      mainOneTimeRadio.click();
      mainOneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  

  updateSubscriptionPricing() {
    // Read prices directly from the main section (already calculated by subscription-widget.js)
    // Use productFormComponent to scope the search if available
    const root = this.productFormComponent || document;
    let mainOneTimePriceElement = root.querySelector('[data-widget-price-onetime]');
    
    // Try to find OTP specific sub-price first (discounted OTP)
    const otpBlock = root.querySelector('.rc_block__type__onetime');
    if (otpBlock) {
      const otpSubPrice = otpBlock.querySelector('[data-widget-price-sub-price]');
      if (otpSubPrice && otpSubPrice.textContent.trim()) {
        mainOneTimePriceElement = otpSubPrice;
      } else {
        // Fallback to checking data-widget-price-onetime inside the OTP block
        const otpMainPrice = otpBlock.querySelector('[data-widget-price-onetime]');
        if (otpMainPrice) {
           mainOneTimePriceElement = otpMainPrice;
        }
      }
    }

    const mainSubscriptionPriceElement = root.querySelector('.rc_block_type_autodeliver [data-widget-price-sub-price]') || root.querySelector('[data-widget-price-sub-price]');
    
    let currentVariantPrice = null;
    let subscriptionPrice = null;
    
    // Get one-time price from main section
    if (mainOneTimePriceElement && mainOneTimePriceElement.textContent) {
      const priceText = mainOneTimePriceElement.textContent.trim();
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (priceMatch) {
        const priceNumber = parseFloat(priceMatch[0].replace(/,/g, ''));
        currentVariantPrice = Math.round(priceNumber * 100);
      }
    }
    
    // Get subscription price from main section (if subscription is available)
    if (mainSubscriptionPriceElement && mainSubscriptionPriceElement.textContent) {
      const priceText = mainSubscriptionPriceElement.textContent.trim();
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (priceMatch) {
        const priceNumber = parseFloat(priceMatch[0].replace(/,/g, ''));
        subscriptionPrice = Math.round(priceNumber * 100);
      }
    }
    
    // Fallback: if no subscription price element found, use one-time price
    if (!subscriptionPrice && currentVariantPrice) {
      subscriptionPrice = currentVariantPrice;
    }
    
    // Fallback to sticky dropdown price ONLY if BOTH prices are missing
    if (!currentVariantPrice && !subscriptionPrice) {
      currentVariantPrice = this.getCurrentVariantPrice();
      subscriptionPrice = currentVariantPrice;
    }
    
    // If we have subscription price but no one-time, use subscription as fallback for one-time
    if (subscriptionPrice && !currentVariantPrice) {
      currentVariantPrice = this.getCurrentVariantPrice();
    }
    
    if (!currentVariantPrice || !subscriptionPrice) {
      return;
    }

    const wrapper = document.querySelector('[id^="subscription-variant--"]:not([style*="display: none"])')
      || document.querySelector('[id^="subscription-variant--"]');
    const priceContainer = document.querySelector('[ref="priceContainer"]');
    const stampedSub = parseInt(
      wrapper?.dataset?.subscriptionCents || priceContainer?.dataset?.subscriptionCents || this.dataset.subscriptionCents || '',
      10
    );
    const stampedCompare = parseInt(
      wrapper?.dataset?.compareCents || priceContainer?.dataset?.compareCents || this.dataset.compareCents || '',
      10
    );
    if (!isNaN(stampedSub) && stampedSub > 0) {
      subscriptionPrice = stampedSub;
    }
    const compareCents = !isNaN(stampedCompare) && stampedCompare > 0
      ? stampedCompare
      : currentVariantPrice;

    const oneTimePriceElement = this.querySelector('label[for="sticky-onetime"] .subscription-price');
    if (oneTimePriceElement) {
      oneTimePriceElement.textContent = this.formatMoney(currentVariantPrice);
    }

    const subscriptionPriceElement = this.querySelector('label[for="sticky-subscription"] .subscription-price');
    if (subscriptionPriceElement) {
      subscriptionPriceElement.textContent = this.formatMoney(subscriptionPrice);
    }

    const subscriptionOriginalPriceElement = this.querySelector('label[for="sticky-subscription"] .subscription-original-price');
    if (subscriptionOriginalPriceElement) {
      if (compareCents > subscriptionPrice) {
        subscriptionOriginalPriceElement.textContent = this.formatMoney(compareCents);
        subscriptionOriginalPriceElement.hidden = false;
      } else {
        subscriptionOriginalPriceElement.textContent = '';
        subscriptionOriginalPriceElement.hidden = true;
      }
    }

    const savingsEl = this.querySelector('label[for="sticky-subscription"] [data-sticky-savings]');
    if (savingsEl) {
      const savingsCents = compareCents - subscriptionPrice;
      if (savingsCents > 0) {
        const template = savingsEl.dataset.savingsTemplate || '';
        savingsEl.hidden = false;
        savingsEl.textContent = template.replace('__AMOUNT__', this.formatMoney(savingsCents));
      } else {
        savingsEl.hidden = true;
        savingsEl.textContent = '';
      }
    }
  }

  /**
   * 
   * @param { {featured_media: {preview_image: {src: string, alt: string} }, title: string} } variant 
   */
  updateStickyProductImage(variant) {
    const productImage = this.querySelector('.sticky-product-image');
    if (productImage instanceof HTMLImageElement && variant.featured_media && variant.featured_media.preview_image) {
      productImage.src = variant.featured_media.preview_image.src;
      productImage.alt = variant.title;
    }
  }

  getCurrentSubscriptionState() {
    const mainPurchaseTypeRadio = document.querySelector('input#rc_purchase_type_onetime');
    if (!mainPurchaseTypeRadio) return false;

    const mainSubscriptionRadio = document.querySelector('input[name="purchase_type"][value="autodeliver"]:checked');
    if (mainSubscriptionRadio) return true;

    const stickySubscriptionRadio = this.querySelector('input[name="sticky_purchase_type"][value="subscription"]:checked');
    if (stickySubscriptionRadio) return true;

    const mainOneTimeRadio = document.querySelector('input[name="purchase_type"][value=""]:checked');
    const stickyOneTimeRadio = this.querySelector('input[name="sticky_purchase_type"][value="onetime"]:checked');
    if (mainOneTimeRadio || stickyOneTimeRadio) {
      return false;
    }
    return false;
  }

  /**
   * 
   * @param {HTMLSelectElement} selectElement 
   */
  updateSelectedFlavourFromSticky(selectElement) {
    // Only update flavour for flavour option changes
    const optionName = selectElement.getAttribute('data-option-name');
    if (optionName && optionName.toLowerCase() === 'flavour') {
      const selectedValue = selectElement.value;
      // Update ALL flavour labels in the sticky bar
      const flavourLabels = this.querySelectorAll('.flavour-label');
      
      flavourLabels.forEach((flavourLabel) => {
        if (flavourLabel && selectedValue) {
          flavourLabel.textContent = selectedValue;
        }
      });
      
      if (flavourLabels.length > 0 && selectedValue) {
      }
    }
  }

  updateFlavourFromMainSection() {
    // Find the checked flavour radio button in main section
    const mainFlavourRadio = document.querySelector('input[type="radio"][name*="Flavour"]:checked');
    // Update ALL flavour labels in the sticky bar
    const flavourLabels = this.querySelectorAll('.flavour-label');
    
    if (mainFlavourRadio instanceof HTMLInputElement && flavourLabels.length > 0) {
      const selectedValue = mainFlavourRadio.value;
      if (selectedValue) {
        flavourLabels.forEach((flavourLabel) => {
          flavourLabel.textContent = selectedValue;
        });
      }
    }
  }

  getCurrentVariantId() {
    // Get current variant ID from the main form
    const variantIdInput = document.querySelector('input[name="id"]');
    return variantIdInput instanceof HTMLInputElement ? parseInt(variantIdInput.value) : null;
  }

  getCurrentVariantPrice() {
    // Look for selected options in all select dropdowns
    const selectElements = this.querySelectorAll('.variant-option__select');
    
    for (const selectElement of selectElements) {
      if (selectElement instanceof HTMLSelectElement) {
        const selectedOption = selectElement.options[selectElement.selectedIndex];
        if (selectedOption) {
          const variantPrice = selectedOption.getAttribute('data-variant-price');
          if (variantPrice) {
            return parseInt(variantPrice);
          }
        }
      }
    }
    
    return null;
  }

  /**
   * 
   * @param {number} cents 
   */
  formatMoney(cents) {
    // Simple money formatting - you can enhance this later
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * 
   * @param {string} optionName 
   * @param {string} optionValue 
   */
  syncMainVariantSelection(optionName, optionValue) {
    
    // Strategy 1: Use variant-picker's updateSelectedOption method directly
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      // First, find the target element by option value
      let targetElement = null;
      document.addEventListener("product-form:variant:update", (ev) => {
        if (this.addToCartButton instanceof HTMLButtonElement && this.addToCartButton.dataset.available === 'true') {
          this.enableAtcButton();
        } 
      });
      
      // Try to find by data-option-value-id first (most reliable)
      const allOptions = variantPicker.querySelectorAll('[data-option-value-id]');
      for (const option of allOptions) {
        if (option instanceof HTMLOptionElement && (option.value === optionValue || option.textContent?.trim() === optionValue)) {
          targetElement = option;
          break;
        }
      }
      
      // Fallback: try to find by value attribute
      if (!targetElement) {
        targetElement = variantPicker.querySelector(`[value="${optionValue}"]`);
      }
      
      // If we found the target element, use variant-picker's method
      if (targetElement instanceof HTMLElement && variantPicker && 'updateSelectedOption' in variantPicker) {
        try {
          // @ts-ignore - Custom method on variant-picker web component
          variantPicker.updateSelectedOption(targetElement);
          
          // Trigger the change event to complete the variant change process
          const changeEvent = new Event('change', { bubbles: true });
          Object.defineProperty(changeEvent, 'target', { value: targetElement });
          variantPicker.dispatchEvent(changeEvent);
          this.updateButtonState();
          return;
        } catch (error) {
        }
      }
    }
    
    // Strategy 2: Try select elements with standard Shopify naming
    let mainOptionSelector = document.querySelector(`select[name="options[${optionName}]"]`);
    
    if (mainOptionSelector instanceof HTMLSelectElement) {
      if (mainOptionSelector.value !== optionValue) {
      mainOptionSelector.value = optionValue;
        
        // Trigger events
      mainOptionSelector.dispatchEvent(new Event('change', { bubbles: true }));
        mainOptionSelector.dispatchEvent(new Event('input', { bubbles: true }));
        this.triggerVariantUpdate();
      }
      this.updateButtonState();
      return;
    }
    
    // Strategy 3: Try radio buttons with theme-specific naming pattern
    if (variantPicker) {
      const blockId = variantPicker.getAttribute('data-block-id');
      const productId = variantPicker.getAttribute('data-product-id');
      
      if (blockId && productId) {
        // Try the theme-specific radio button naming pattern
        const radioName = `${optionName}-${blockId}-${productId}`;
        const radioButton = document.querySelector(`input[type="radio"][name="${radioName}"][value="${optionValue}"]`);
        
        if (radioButton instanceof HTMLInputElement && !radioButton.checked) {
          radioButton.checked = true;
          radioButton.dispatchEvent(new Event('change', { bubbles: true }));
          radioButton.dispatchEvent(new Event('input', { bubbles: true }));
          
          this.triggerVariantUpdate();
          this.updateButtonState();
          return;
        } else if (radioButton) {
          this.updateButtonState();
          return;
        }
      }
    }
    
    // Strategy 4: Try standard radio button naming
    const standardRadioButton = document.querySelector(`input[type="radio"][name="options[${optionName}]"][value="${optionValue}"]`);
    
    if (standardRadioButton instanceof HTMLInputElement && !standardRadioButton.checked) {
      standardRadioButton.checked = true;
      standardRadioButton.dispatchEvent(new Event('change', { bubbles: true }));
      standardRadioButton.dispatchEvent(new Event('input', { bubbles: true }));
      
      this.triggerVariantUpdate();
      this.updateButtonState();
      return;
    } else if (standardRadioButton) {
      this.updateButtonState();
      return;
    }
    
    // Strategy 5: Try to find any radio button with the value (wildcard search)
    const allRadioButtons = document.querySelectorAll(`input[type="radio"][value="${optionValue}"]`);
    
    for (let i = 0; i < allRadioButtons.length; i++) {
      const radio = allRadioButtons[i];
      if (!(radio instanceof HTMLInputElement)) continue;
      const radioName = radio.name;
      
      // Check if this radio button name contains the option name
      if (radioName.toLowerCase().includes(optionName.toLowerCase()) && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        radio.dispatchEvent(new Event('input', { bubbles: true }));
        
        this.triggerVariantUpdate();
        this.updateButtonState();
        return;
      }
    }
    
    console.warn('❌ StickyAddToCart: Could not sync variant:', optionName, optionValue);
    this.updateButtonState();
  }

  syncStickySubscriptionToMainForm() {
    // Get sticky subscription selection
    const stickySubscriptionRadio = this.querySelector('input[name="sticky_purchase_type"]:checked');
    
    if (!(stickySubscriptionRadio instanceof HTMLInputElement)) return;

    const isSubscription = stickySubscriptionRadio.value === 'subscription';
    
    // Update main form subscription selection
    const mainOneTimeRadio = document.querySelector('input[name="purchase_type"][value=""]');
    const mainSubscriptionRadio = document.querySelector('input[name="purchase_type"][value="autodeliver"]');
    
    if (isSubscription && mainSubscriptionRadio instanceof HTMLInputElement) {
      mainSubscriptionRadio.checked = true;
      mainSubscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (!isSubscription && mainOneTimeRadio instanceof HTMLInputElement) {
      mainOneTimeRadio.checked = true;
      mainOneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  triggerVariantUpdate() {
    // The variant-picker handles updates automatically through its change event listener
    // We don't need to manually trigger updates since updateSelectedOption() + change event does it all
    const variantPicker = document.querySelector('variant-picker');
    
    if (variantPicker) {
      // The variant picker automatically handles updates when we call updateSelectedOption() and dispatch change event
      // Just dispatch additional events for any other components that might be listening
      variantPicker.dispatchEvent(new CustomEvent('variant:update', { bubbles: true }));
      variantPicker.dispatchEvent(new CustomEvent('variant:change', { bubbles: true }));
    }
    
    // Fallback: Force update variant-dependent elements by triggering events
    const productForm = document.querySelector('form[action*="/cart/add"]');
    
    if (productForm) {
      // Trigger form change event
      productForm.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Look for variant picker elements and trigger their update
      if (variantPicker) {
        variantPicker.dispatchEvent(new CustomEvent('variant:update', { bubbles: true }));
        variantPicker.dispatchEvent(new CustomEvent('variant:change', { bubbles: true }));
      }
      
      // Look for product form elements and trigger their update
      const productFormElement = document.querySelector('product-form, [data-product-form]');
      
      if (productFormElement) {
        productFormElement.dispatchEvent(new CustomEvent('variant:update', { bubbles: true }));
      }
      
      // Trigger a global variant change event for any other listeners
      document.dispatchEvent(new CustomEvent('product:variant:change', { 
        bubbles: true,
        detail: { source: 'sticky-add-to-cart' }
      }));
    }
  }

  updateButtonState() {
    if (!(this.addToCartButton instanceof HTMLButtonElement)) return;
    
    // Use the main form's ATC button as the source of truth.
    // The per-option-value data attributes (data-variant-available, data-variant-sold-out-metafield)
    // map to the first variant matching that option value, not the actual selected variant combination.
    const mainButton = document.querySelector('product-form-component [ref="addToCartButton"]');
    if (mainButton instanceof HTMLButtonElement) {
      const isSoldOut = mainButton.disabled;
      this.addToCartButton.disabled = isSoldOut;
      this.addToCartButton.setAttribute('aria-label', isSoldOut ? 'Sold out' : 'Add to cart');
      this.addToCartButton.dataset.available = !isSoldOut ? 'true' : 'false';
      
      const buttonText = this.addToCartButton.querySelector('.sticky-add-to-cart__button-text');
      if (buttonText) {
        buttonText.textContent = isSoldOut ? 'Sold Out' : 'Add to Cart';
      }
      return;
    }
    
    // Fallback: check option selectors when main button isn't available
    const optionSelectors = this.querySelectorAll('select[name^="options["]');
    let hasUnavailableOption = false;
    
    optionSelectors.forEach((selector) => {
      if (!(selector instanceof HTMLSelectElement)) return;
      const selectedOption = selector.options[selector.selectedIndex];
      
      if (selectedOption && (selectedOption.getAttribute('data-variant-available') ?? '').includes('false')) {
        hasUnavailableOption = true;
      }
    });
    
    if (hasUnavailableOption) {
      this.addToCartButton.disabled = true;
    }
    this.addToCartButton.setAttribute('aria-label', hasUnavailableOption ? 'Sold out' : 'Add to cart');
    this.addToCartButton.dataset.available = !hasUnavailableOption ? 'true' : 'false';
    
    const buttonText = this.addToCartButton.querySelector('.sticky-add-to-cart__button-text');
    if (buttonText) {
      buttonText.textContent = hasUnavailableOption ? 'Sold Out' : 'Add to Cart';
    }
  }

  forceRefreshVariants() {
    // Fallback method to force refresh variant-dependent elements
    
    // Trigger a page refresh as last resort (only if absolutely necessary)
    const shouldRefresh = confirm('Variant synchronization failed. Refresh the page to fix this issue?');
    if (shouldRefresh) {
      window.location.reload();
    }
  }

  /**
   * 
   * @param {string} message 
   */
  announceToScreenReader(message) {
    // Create a live region for screen reader announcements
    let liveRegion = document.getElementById('sticky-add-to-cart-announcement');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'sticky-add-to-cart-announcement';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      document.body.appendChild(liveRegion);
    }
    
    liveRegion.textContent = message;
    
    // Clear the message after announcement
    setTimeout(() => {
      if(liveRegion instanceof HTMLDivElement) {
        liveRegion.textContent = '';
      }
    }, 1000);
  }

  /**
   * Shop now button mobile handle
  */
  // shopNowHandle() {
  //   this.shopNowButton?.addEventListener('click', () => {
  //     if(this.shopNowContainer?.hasAttribute('data-mobile-expanded')) {
  //       this.shopNowContainer?.removeAttribute('data-mobile-expanded');
  //     } else {
  //       this.shopNowContainer?.setAttribute('data-mobile-expanded', "true");
  //     }
  //   })

  //   window.addEventListener('resize', () => {
  //     this.shopNowContainer?.removeAttribute('data-mobile-expanded');
  //   })
  // }

  shopNowHandle() {
  const mobileShopNowEl = this.querySelector('.sticky-add-to-cart__mobile-shop-now');

  this.shopNowButton?.addEventListener('click', () => {
    if (this.shopNowContainer?.hasAttribute('data-mobile-expanded')) {
      this.shopNowContainer?.removeAttribute('data-mobile-expanded');
      if (mobileShopNowEl instanceof HTMLElement) mobileShopNowEl.style.display = "flex";
    } else {
      this.shopNowContainer?.setAttribute('data-mobile-expanded', "true");
      if (mobileShopNowEl instanceof HTMLElement) mobileShopNowEl.style.display = "none";
    }
  });

  window.addEventListener('resize', () => {
    this.shopNowContainer?.removeAttribute('data-mobile-expanded');
    if (mobileShopNowEl instanceof HTMLElement) mobileShopNowEl.style.display = "flex";
  });
}

  optionSelectorHandle() {
    let syncing = false; // guard flag

    /**
     * 
     * @param {HTMLSelectElement} source 
     * @param {Element[]} targets 
     * @returns 
     */
    const syncSelects = (source, targets) => {
      if (syncing) return; // prevent recursive loop
      syncing = true;

      targets.forEach(target => {
        if (!(target instanceof HTMLSelectElement)) return;
        if (source.name.toLowerCase() === target.name.toLowerCase() && source !== target) {
          target.value = source.value;
          // only dispatch if you REALLY need downstream listeners
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      syncing = false;
    };

    if (!(this.selectOptionSelectors instanceof NodeList)) return;
    Array.from(this.selectOptionSelectors).forEach((op) => {
      if (!(op instanceof HTMLSelectElement)) return;
      op.addEventListener('change', () => {
        if (!(this.thisOptionSelectors instanceof NodeList)) return;
        syncSelects(op, Array.from(this.thisOptionSelectors));
      });
    });

    if (!(this.thisOptionSelectors instanceof NodeList)) return;
    Array.from(this.thisOptionSelectors).forEach(op => {
      if (!(op instanceof HTMLSelectElement)) return;
      op.addEventListener('change', () => {
        syncSelects(op, Array.from(this.selectOptionSelectors));
      });
    });
  }


  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    // Remove body padding
    document.body.style.paddingBottom = '';
  }
}

if(!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}